/**
 * Unsubscribe service — the only unauthenticated surface in the SDK whose
 * credential travels in the query string.
 *
 * The caller is a recipient who clicked a footer link in an email. They have
 * no session (they may not even have an account), so `users.unsubscribe_token`
 * identifies them. It authorises exactly one action — flipping one
 * `settings.notifications.categories.*` boolean on its own row — which is what
 * makes it safe to carry in a URL.
 *
 * `preview` is a pure read and `confirm` performs the change. That split is a
 * security control, not a convention: the same URL is fetched unattended by
 * mail scanners and link previewers, so landing on the page must never be what
 * opts someone out.
 */

import { TonnieClient } from '../client/base';
import { API_ENDPOINTS } from '../client/config';
import type { NotificationBucket } from '@tonnie/types';

export interface UnsubscribePreview {
  /** Masked (`jos***@tonnie.com`) — recognisable without exposing the address. */
  emailMasked: string;
  bucket: NotificationBucket;
  /** Already off, so the page can say so instead of offering a no-op button. */
  alreadyUnsubscribed: boolean;
}

export interface UnsubscribeResult {
  unsubscribed: true;
  bucket: NotificationBucket;
}

export class UnsubscribeService {
  constructor(private readonly client: TonnieClient) {}

  /** Read-only. Safe to call on page load. */
  async preview(token: string, bucket: NotificationBucket): Promise<UnsubscribePreview> {
    return this.client.get<UnsubscribePreview>(API_ENDPOINTS.UNSUBSCRIBE.ROOT, {
      params: { token, bucket },
    });
  }

  /** Performs the opt-out. Idempotent — a second confirm succeeds. */
  async confirm(token: string, bucket: NotificationBucket): Promise<UnsubscribeResult> {
    // Body is empty on purpose: the API ignores it. An RFC 8058 mail client
    // posts `List-Unsubscribe=One-Click` here instead, and the endpoint must
    // honour both callers, so neither body can be meaningful input.
    return this.client.post<UnsubscribeResult>(API_ENDPOINTS.UNSUBSCRIBE.ROOT, undefined, {
      params: { token, bucket },
    });
  }
}

export function createUnsubscribeService(client: TonnieClient): UnsubscribeService {
  return new UnsubscribeService(client);
}
